import { useQuery, useQueryClient } from '@tanstack/vue-query';
import { useIntervalFn, refDebounced } from '@vueuse/core';
import { defineStore } from 'pinia';
import { useStore } from '@/store/useStore';
import type { Document } from '@/modules/documents/types';
import type { FileModel } from '@@/packages/shared/types/models/file';
import { fetchUploads } from './api/fetchUploads';

const UPLOADS_QUERY = '/uploads';

/**
 * This store consolidates file requests made by the useUploads composable in
 * order to reduce the number of requests sent.
 *
 * In effect, it enabled devs to use useUploads() however they want to without
 * having to worry about how many requests are being sent.
 *
 * It also handles polling for file scans when necessary.
 */
export const useUploadsStore = defineStore('uploads', () => {
  const watchedUploadIDs = ref<string[]>([]);
  const debouncedUploadIDs = refDebounced(watchedUploadIDs, 100);

  // TODO: remove old queries from cache - they won't be needed again (1 hour)
  const query = useQuery({
    queryKey: [UPLOADS_QUERY, debouncedUploadIDs] as const,
    async queryFn({ queryKey }) {
      if (!queryKey[1].length) return {};
      const res = await fetchUploads(queryKey[1]);
      return res.data.files;
    },
  });

  const uploads = computed(() => query.data.value ?? {});

  function subscribe(uploadIDs: MaybeRefOrGetter<(string | Document)[]>) {
    watch(
      toRef(uploadIDs),
      (newUploadIDs) => {
        const unwatchedIDs = newUploadIDs
          .map((idOrDoc) => (typeof idOrDoc === 'string' ? idOrDoc : idOrDoc?.uploadID))
          .filter((id) => !watchedUploadIDs.value.includes(id));

        // We can't use .push() because it mutates the array and breaks the debouncing
        watchedUploadIDs.value = watchedUploadIDs.value.concat(unwatchedIDs);
      },
      { immediate: true, deep: true },
    );
  }

  const queryClient = useQueryClient();

  // If files are being scanned, poll for updates
  useIntervalFn(async () => {
    if (!query.data.value) return;
    const scanningFileIDs = Object.values(uploads.value)
      .filter(
        (file) =>
          !file.latestScan ||
          (file.latestScan.status !== 'rejected' && file.latestScan.status !== 'approved'),
      )
      .map((file) => file.id);

    if (!scanningFileIDs.length) return;

    const res = await fetchUploads(scanningFileIDs);

    queryClient.setQueryData(
      // TODO: is it safe to use debouncedUploadsIDs or should we calculate the IDs?
      [UPLOADS_QUERY, debouncedUploadIDs],
      (existingFiles: Record<string, FileModel>) =>
        existingFiles &&
        ({
          ...existingFiles,
          ...res.data.files,
        } satisfies Record<string, FileModel>),
    );
  }, 2e3);

  // Sync with vuex so legacy code can get uploads from there
  const store = useStore();
  watchEffect(() => {
    if (store) store.commit('uploads/files/SET_FILES', uploads.value);
  });

  return { subscribe, uploads };
});
